import type { FunctionComponent, HTMLAttributes } from "preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { preventDefaultCallback } from "../../components/utils.ts";
import { useLocalTimeISOString } from "../../hooks/local_time_iso_string.ts";
import { FarpovCameraSelect } from "../farpov_camera_select/farpov_camera_select.tsx";
import { PhotoImage } from "../photo_image/photo_image.tsx";
import { getPhotos, getPhotosWithTotal } from "../../store/task/photos.ts";
import type { PhotoInfo } from "../../store/task/photos.ts";
import { Icon } from "../icon/icon.tsx";
import { getRequestUrl, URL_BASE, requestDelete } from "../../utils/request.ts";
import { useStore } from "@nanostores/preact";
import { $isSubAccount, $isLogined, refreshUserInfo } from "../../store/user_info.ts";
import { usePageRefresh, useRequestScope } from "../../hooks/use_page_refresh.ts";
import { invalidateCameraInfo, updateCameraInfo, $cameraList, $cameraListLoaded } from "../../store/cameras.ts";
import { invalidateAI } from "../../store/ai.ts";
import { isRefreshCancelled } from "../../utils/refresh_resource.ts";
import { navigate } from "../../router_utils.ts";

export type PhotoGridProps = HTMLAttributes<HTMLDivElement> & {
    cameraId?: number;
    initSelectTime?: number;
    hideCameraSelector?: boolean;
    onPhotoSelected?: (cameraId: number, photo: PhotoInfo) => unknown;
};

const PAGE_SIZE = 12;

export const PhotoGrid: FunctionComponent<PhotoGridProps> = (props) => {
    const selectedTime = useLocalTimeISOString(props.initSelectTime ?? Date.now());
    const [selectedCameraId, setSelectedCameraId] = useState(props.cameraId ?? -1);
    const [photoList, setPhotoList] = useState([] as PhotoInfo[]);
    const [isBusy, setIsBusy] = useState(false);
    const [selectedPhotos, setSelectedPhotos] = useState<Set<number>>(new Set());
    const [totalPhotos, setTotalPhotos] = useState(0); // Track total count
    const [activePhotoId, setActivePhotoId] = useState<number>(-1);
    const [expandedPhotoId, setExpandedPhotoId] = useState<number>(-1);
    const photoItemMapRef = useRef(new Map<number, HTMLDivElement>());
    
    const isSubAccount = useStore($isSubAccount);
    
    // Range Download State
    const [rangeStart, setRangeStart] = useState("");
    const [rangeEnd, setRangeEnd] = useState("");
    const [showRangeModal, setShowRangeModal] = useState(false);
    
    // Delete All Modal State
    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
    const [deleteAllConfirmInput, setDeleteAllConfirmInput] = useState("");

    const t = useTranslator();

    const [viewingPhoto, setViewingPhoto] = useState<PhotoInfo | null>(null);
    const isLogined = useStore($isLogined);
    const cameras = useStore($cameraList);
    const camerasLoaded = useStore($cameraListLoaded);
    const requestScope = useRequestScope(`photos:${selectedCameraId}`);
    const [loadError, setLoadError] = useState("");
    const [isLatest, setIsLatest] = useState(true);
    const queryRef = useRef<{ before: number; cursor?: string; reverse: boolean }>({ before: 0, reverse: false });

    const renderDetailModal = () => {
        if (!viewingPhoto) return null;
        const analysis = viewingPhoto.analysis?.['construction_monitor'];
        
        return (
            <dialog open onClick={(e) => {
                 if (e.target === e.currentTarget) setViewingPhoto(null);
            }}>
                <article style={{ maxWidth: "90vw", width: "1000px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                    <header>
                        <button aria-label="Close" rel="prev" onClick={() => setViewingPhoto(null)}></button>
                        <p>
                            <strong>{viewingPhoto.photo_name}</strong>
                        </p>
                    </header>
                    <div style={{ display: "flex", flex: 1, minHeight: 0, gap: "1rem", flexDirection: "row", flexWrap: "wrap" }}>
                        <div style={{ flex: "2 1 400px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#000", minHeight: "300px" }}>
                             <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <PhotoImage photoId={viewingPhoto.photo_id} mode="view" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                             </div>
                        </div>
                        <div style={{ flex: "1 1 200px", overflowY: "auto", padding: "0.5rem" }}>
                            <h5>{t({ zh: "AI 分析结果", en: "AI Analysis" })}</h5>
                            {!analysis ? (
                                <p>{t({ zh: "暂无分析数据", en: "No analysis data available" })}</p>
                            ) : (
                                <div>
                                    <div class="grid" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                                        <div style={{ textAlign: "center", padding: "1rem", background: "var(--pico-card-background-color)", borderRadius: "8px" }}>
                                            <div style={{ fontSize: "2rem" }}>👷</div>
                                            <div>{analysis.summary.person}</div>
                                            <small>{t({ zh: "人员", en: "Person" })}</small>
                                        </div>
                                        <div style={{ textAlign: "center", padding: "1rem", background: "var(--pico-card-background-color)", borderRadius: "8px" }}>
                                            <div style={{ fontSize: "2rem" }}>🚛</div>
                                            <div>{analysis.summary.vehicle}</div>
                                            <small>{t({ zh: "车辆", en: "Vehicle" })}</small>
                                        </div>
                                    </div>
                                    <hr />
                                    <h6>{t({ zh: "详细列表", en: "Details" })}</h6>
                                    <table class="striped">
                                        <thead>
                                            <tr>
                                                <th>{t({ zh: "类型", en: "Type" })}</th>
                                                <th>{t({ zh: "置信度", en: "Conf" })}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {analysis.objects?.map((obj: any, idx: number) => (
                                                <tr key={idx}>
                                                    <td>{obj.label}</td>
                                                    <td>{Math.round(obj.confidence * 100)}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </article>
            </dialog>
        );
    };

    const loadPhotos = async (before: number, cursor?: string, reverse = false, latest = false, afterDelete = false) => {
        if (selectedCameraId <= 0) return;
        const request = requestScope.begin();
        try {
            setIsBusy(true);
            setLoadError("");
            const [result, total] = await Promise.all([
                getPhotosWithTotal(selectedCameraId, before, 0, PAGE_SIZE, reverse, cursor, request.signal),
                getPhotosWithTotal(selectedCameraId, Date.now() / 1000, 0, 1, false, undefined, request.signal),
            ]);
            request.check();
            let photos = result.photos;
            if ((reverse && photos.length < PAGE_SIZE) || (afterDelete && !photos.length)) {
                before = Date.now() / 1000;
                cursor = undefined;
                reverse = false;
                latest = true;
                photos = await getPhotos(selectedCameraId, before, 0, PAGE_SIZE, false, undefined, request.signal);
                request.check();
            }
            if (!photos.length && cursor && !afterDelete) return;
            setPhotoList(photos);
            setTotalPhotos(total.total);
            setSelectedPhotos(new Set()); 
            setIsLatest(latest);
            queryRef.current = { before, cursor, reverse };
        } catch (error) {
            if (request.current() && !isRefreshCancelled(error)) {
                setLoadError(error instanceof Error ? error.message : String(error));
            }
            throw error;
        } finally {
            request.finish();
            if (request.current()) setIsBusy(false);
        }
    };
    const updateLatestPhotoList = () => loadPhotos(Date.now() / 1000, undefined, false, true);
    const pageRefresh = usePageRefresh({
        pageKey: `photos:${selectedCameraId}`, enabled: isLogined && selectedCameraId > 0,
        canAutoRefresh: isLatest && selectedPhotos.size === 0 && !viewingPhoto && !isBusy && !showRangeModal && !showDeleteAllModal,
        refresh: async () => {
            await refreshUserInfo();
            await updateLatestPhotoList();
        },
    });

    useEffect(() => {
        if ((props.cameraId ?? -1) != selectedCameraId) {
            // parent cameraid changed
            setSelectedCameraId(props.cameraId ?? -1);
        }
    }, [props.cameraId]);

    useEffect(() => {
        // on mount, camera change
        if (selectedCameraId < 0) {
            setPhotoList([]);
            return;
        }
        setPhotoList([]);
        setSelectedPhotos(new Set());
        setViewingPhoto(null);
        setTotalPhotos(0);
        setIsLatest(true);
    }, [selectedCameraId]);
    useEffect(() => {
        if (camerasLoaded && selectedCameraId > 0 && !cameras.some(c => c.camera_id === selectedCameraId)) {
            requestScope.cancel();
            setSelectedCameraId(-1);
            setPhotoList([]);
            setSelectedPhotos(new Set());
            setViewingPhoto(null);
            setTotalPhotos(0);
            setLoadError(t({ zh: "当前相机已移除或无权访问", en: "Camera removed or access denied" }));
        }
    }, [cameras, camerasLoaded, selectedCameraId]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!("IntersectionObserver" in window)) return;
        if (!window.matchMedia("(hover: none)").matches) return;

        let rafId = 0;
        const visible = new Map<number, { ratio: number; midY: number }>();
        const thresholds = [0, 0.1, 0.25, 0.5, 0.75, 1];

        const chooseActive = () => {
            rafId = 0;
            if (visible.size <= 0) {
                setActivePhotoId(-1);
                return;
            }
            const centerY = window.innerHeight / 2;
            let bestId = -1;
            let bestScore = -Infinity;
            for (const [id, v] of visible) {
                const dist = Math.abs(v.midY - centerY);
                const score = v.ratio * 1000 - dist;
                if (score > bestScore) {
                    bestScore = score;
                    bestId = id;
                }
            }
            setActivePhotoId(bestId);
        };

        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const el = entry.target as HTMLDivElement;
                const photoIdStr = el.getAttribute("data-photo-id") ?? "";
                const id = Number.parseInt(photoIdStr, 10);
                if (!Number.isFinite(id) || id <= 0) continue;

                if (!entry.isIntersecting || entry.intersectionRatio <= 0) {
                    visible.delete(id);
                    continue;
                }

                const rect = entry.boundingClientRect;
                visible.set(id, { ratio: entry.intersectionRatio, midY: rect.top + rect.height / 2 });
            }
            if (rafId) return;
            rafId = window.requestAnimationFrame(chooseActive);
        }, { root: null, threshold: thresholds });

        for (const photo of photoList) {
            const el = photoItemMapRef.current.get(photo.photo_id);
            if (el) observer.observe(el);
        }
        rafId = window.requestAnimationFrame(chooseActive);

        return () => {
            if (rafId) window.cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, [photoList]);

    useEffect(() => {
        if (expandedPhotoId < 0) return;
        setExpandedPhotoId(-1);
    }, [activePhotoId]);

    const gotoAction = () => loadPhotos(selectedTime.unixTimestampMs / 1000).catch(() => {});
    const nextPageAction = () => photoList.length > 0 &&
        loadPhotos(Date.now() / 1000, photoList.at(-1)!.photo_cursor).catch(() => {});
    const previousPageAction = () => photoList.length > 0 &&
        loadPhotos(Date.now() / 1000, photoList[0].photo_cursor, true).catch(() => {});
    const refreshAfterDelete = async () => {
        invalidateCameraInfo();
        invalidateAI();
        const query = queryRef.current;
        const results = await Promise.allSettled([
            loadPhotos(isLatest ? Date.now() / 1000 : query.before, query.cursor, query.reverse, isLatest, true),
            updateCameraInfo(),
        ]);
        if (results.some(result => result.status === "rejected")) {
            setLoadError(t({ zh: "删除成功，数据刷新失败，请重试", en: "Deleted; refresh failed. Please retry." }));
        }
    };

    const handleDownload = (photoId: number, photoName: string) => {
        // Use the new redirect API for robust downloading
        const url = getRequestUrl(`/v2/camera/photo/${photoId}`);
        // Create a hidden iframe or just navigate current window?
        // Anchor with download attribute works best for attachments if backend sets headers.
        const a = document.createElement("a");
        a.href = url;
        // The backend sets Content-Disposition: attachment, so browser will handle it.
        // We don't strictly need the 'download' attribute if it's cross-origin redirect,
        // but it doesn't hurt.
        a.download = photoName;
        a.target = "_blank"; // Open in new tab to avoid disrupting SPA if redirect is slow
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const toggleSelection = (id: number) => {
        const newSet = new Set(selectedPhotos);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedPhotos(newSet);
    };

    const handleBatchDownload = async () => {
        if (selectedPhotos.size === 0) return;
        setIsBusy(true);
        try {
            // Use raw fetch to get the blob (urls.txt)
            // CSRF token is needed for POST
            const getCookie = (name: string) => {
                let cookieValue = "";
                if (document.cookie && document.cookie !== "") {
                    const cookies = document.cookie.split(";");
                    for (let i = 0; i < cookies.length; i++) {
                        const cookie = cookies[i].trim();
                        if (cookie.substring(0, name.length + 1) === (name + "=")) {
                            cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                            break;
                        }
                    }
                }
                return cookieValue;
            };

            const resp = await fetch(getRequestUrl("/v2/photo_download_urls"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": getCookie("csrftoken"),
                },
                body: JSON.stringify({
                    camera_id: selectedCameraId,
                    photo_ids: Array.from(selectedPhotos)
                })
            });

            if (!resp.ok) {
                throw new Error("Batch download request failed");
            }

            const blob = await resp.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `urls_${Date.now()}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            // Clear selection after successful download request
            setSelectedPhotos(new Set());

        } catch (e) {
            console.error("Batch download failed", e);
            alert(t({ zh: "批量下载失败", en: "Batch download failed" }));
        } finally {
            setIsBusy(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm(t({ zh: "确定删除这张照片吗？", en: "Delete this photo?" }))) return;
        requestScope.cancel();
        setIsBusy(true);
        try {
            await requestDelete("/v2/photo", { photo_id: id });
            // Refresh list
            await refreshAfterDelete();
        } catch (e) {
            alert(t({ zh: "删除失败", en: "Delete Failed" }));
        } finally {
            setIsBusy(false);
        }
    };

    const handleBatchDelete = async () => {
        if (selectedPhotos.size === 0) return;
        if (!confirm(t({ zh: `确定删除选中的 ${selectedPhotos.size} 张照片吗？`, en: `Delete ${selectedPhotos.size} photos?` }))) return;
        
        requestScope.cancel();
        setIsBusy(true);
        try {
            await requestDelete("/v2/photo", { photo_ids: Array.from(selectedPhotos) });
            setSelectedPhotos(new Set());
            await refreshAfterDelete();
        } catch (e) {
             alert(t({ zh: "批量删除失败", en: "Batch Delete Failed" }));
        } finally {
            setIsBusy(false);
        }
    };

    const handleDeleteAll = async () => {
        if (selectedCameraId < 0) return;
        setDeleteAllConfirmInput("");
        setShowDeleteAllModal(true);
    };

    const deleteAllConfirmed = async () => {
        const confirmCode = "DELETE";
        if (deleteAllConfirmInput !== confirmCode) {
            alert(t({ zh: "确认码错误，操作已取消。", en: "Incorrect confirmation code. Operation cancelled." }));
            return;
        }
        requestScope.cancel();
        setIsBusy(true);
        try {
            await requestDelete("/v2/photo", { camera_id: selectedCameraId, delete_all: true });
            setPhotoList([]);
            setSelectedPhotos(new Set());
            setTotalPhotos(0);
            setShowDeleteAllModal(false);
            await refreshAfterDelete();
            alert(t({ zh: "已清空所有照片", en: "All photos cleared" }));
        } catch (e) {
            alert((e as Error)?.message ?? t({ zh: "清空失败", en: "Clear Failed" }));
        } finally {
            setIsBusy(false);
        }
    };

    const toggleSelectAll = () => {
        const newSet = new Set(selectedPhotos);
        const allSelected = photoList.every(p => newSet.has(p.photo_id));
        
        if (allSelected) {
            // Deselect current page
            photoList.forEach(p => newSet.delete(p.photo_id));
        } else {
            // Select current page
            photoList.forEach(p => newSet.add(p.photo_id));
        }
        setSelectedPhotos(newSet);
    };

    const isAllPageSelected = photoList.length > 0 && photoList.every(p => selectedPhotos.has(p.photo_id));

    const handleRangeDownload = async () => {
        const start = parseInt(rangeStart);
        const end = parseInt(rangeEnd);
        
        if (isNaN(start) || isNaN(end) || start < 1 || end < start) {
            alert(t({ zh: "请输入有效的范围", en: "Invalid range" }));
            return;
        }
        
        // Use raw download link via new window
        // The backend GET /v2/photo_download_urls?camera_id=X&from=A&to=B returns urls.txt
        const url = getRequestUrl(`/v2/photo_download_urls?camera_id=${selectedCameraId}&from=${start}&to=${end}`);
        window.open(url, "_blank");
        setShowRangeModal(false);
    };

    const renderRangeModal = () => {
        if (!showRangeModal) return null;
        return (
            <dialog open>
                <article>
                    <header>
                        <button aria-label="Close" rel="prev" onClick={() => setShowRangeModal(false)}></button>
                        <p>
                            <strong>{t({ zh: "批量下载范围", en: "Batch Download Range" })}</strong>
                        </p>
                    </header>
                    <p>
                        {t({ zh: "下载指定数量范围的照片链接列表 (例如 1-100)", en: "Download URL list for range (e.g. 1-100)" })}
                    </p>
                    <div class="grid">
                        <label>
                            {t({ zh: "起始 (第几张)", en: "From (Index)" })}
                            <input 
                                type="number" 
                                value={rangeStart} 
                                onInput={(e) => setRangeStart((e.target as HTMLInputElement).value)} 
                                min="1"
                            />
                        </label>
                        <label>
                            {t({ zh: "结束 (第几张)", en: "To (Index)" })}
                            <input 
                                type="number" 
                                value={rangeEnd} 
                                onInput={(e) => setRangeEnd((e.target as HTMLInputElement).value)} 
                                min="1"
                            />
                        </label>
                    </div>
                    <footer>
                        <button class="secondary" onClick={() => setShowRangeModal(false)}>
                            {t({ zh: "取消", en: "Cancel" })}
                        </button>
                        <button onClick={handleRangeDownload}>
                            {t({ zh: "下载链接列表", en: "Download List" })}
                        </button>
                    </footer>
                </article>
            </dialog>
        );
    };

    const renderDeleteAllModal = () => {
        if (!showDeleteAllModal) return null;
        const confirmCode = "DELETE";
        return (
            <dialog open>
                <article>
                    <header>
                        <button
                            aria-label="Close"
                            rel="prev"
                            onClick={() => {
                                if (isBusy) return;
                                setShowDeleteAllModal(false);
                            }}
                        ></button>
                        <p>
                            <strong>{t({ zh: "清空所有照片", en: "Delete All Photos" })}</strong>
                        </p>
                    </header>
                    <p>
                        {t({
                            zh: "此操作不可恢复，将删除当前相机的所有照片。",
                            en: "This cannot be undone. It will delete ALL photos for this camera.",
                        })}
                    </p>
                    <label>
                        {t({ zh: `请输入 "${confirmCode}" 以确认`, en: `Type "${confirmCode}" to confirm` })}
                        <input
                            type="text"
                            value={deleteAllConfirmInput}
                            onInput={(e) => setDeleteAllConfirmInput((e.target as HTMLInputElement).value)}
                            placeholder={confirmCode}
                            disabled={isBusy}
                        />
                    </label>
                    <footer>
                        <button
                            class="secondary"
                            disabled={isBusy}
                            onClick={() => setShowDeleteAllModal(false)}
                        >
                            {t({ zh: "取消", en: "Cancel" })}
                        </button>
                        <button
                            class="contrast"
                            aria-busy={isBusy}
                            disabled={isBusy || deleteAllConfirmInput !== confirmCode}
                            onClick={deleteAllConfirmed}
                        >
                            {t({ zh: "确定清空", en: "Delete All" })}
                        </button>
                    </footer>
                </article>
            </dialog>
        );
    };

    const renderPhotoGridItem = useMemo(() => (photo: PhotoInfo, idx: number) => {
        const isSelected = selectedPhotos.has(photo.photo_id);
        const isSelectMode = Boolean(props.onPhotoSelected);
        const isExpanded = photo.photo_id === expandedPhotoId;
        return (
            <div
                class={`_ph_ ${isSelected ? "selected" : ""} ${photo.photo_id === activePhotoId ? "ph_active" : ""} ${isExpanded ? "ph_expanded" : ""}`}
                key={photo.photo_id}
                data-photo-id={photo.photo_id}
                ref={(el) => {
                    if (el) photoItemMapRef.current.set(photo.photo_id, el);
                    else photoItemMapRef.current.delete(photo.photo_id);
                }}
                style={isSelectMode ? { cursor: "pointer" } : {}}
                onClick={(e: MouseEvent) => {
                    if (isSelectMode) return;
                    if (typeof window === "undefined") return;
                    if (!window.matchMedia("(hover: none)").matches) return;
                    e.preventDefault();
                    const next = isExpanded ? -1 : photo.photo_id;
                    setExpandedPhotoId(next);
                }}
            >
                <div class="_img_">
                    <PhotoImage photoId={photo.photo_id} mode="thumbnail" />
                    {/* AI Analysis Tags */}
                    {photo.analysis && photo.analysis['construction_monitor'] && (
                        <div style={{
                            position: "absolute",
                            top: "5px",
                            left: "5px",
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                            zIndex: 10
                        }}>
                            {photo.analysis['construction_monitor'].summary?.person > 0 && (
                                <span style={{ backgroundColor: "rgba(255, 193, 7, 0.9)", color: "#000", padding: "2px 6px", borderRadius: "4px", fontSize: "0.8em", fontWeight: "bold", backdropFilter: "blur(2px)" }}>
                                    👷 {photo.analysis['construction_monitor'].summary.person}
                                </span>
                            )}
                            {photo.analysis['construction_monitor'].summary?.vehicle > 0 && (
                                <span style={{ backgroundColor: "rgba(23, 162, 184, 0.9)", color: "#fff", padding: "2px 6px", borderRadius: "4px", fontSize: "0.8em", fontWeight: "bold", backdropFilter: "blur(2px)" }}>
                                    🚛 {photo.analysis['construction_monitor'].summary.vehicle}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <label
                    class="_info_"
                    style={props.onPhotoSelected ? { cursor: "pointer" } : {}}
                    onClick={isSelectMode ? undefined : (props.onPhotoSelected
                        ? props.onPhotoSelected.bind(null, selectedCameraId, photo)
                        : undefined)}
                >
                    <div>
                        {t({
                            zh: "照片时间",
                            en: "Photo Time",
                        })}: {new Date(photo.photo_time * 1000).toLocaleString()}
                    </div>
                    <div>
                        {t({
                            zh: "照片名称",
                            en: "Photo Name",
                        })}: {photo.photo_name}
                    </div>
                </label>

                {/* Checkbox for Selection */}
                {isSelectMode ? null : (
                    <div class="photo_checkbox" onClick={(e) => {
                        e.stopPropagation();
                        toggleSelection(photo.photo_id);
                    }}>
                        {isSelected ? <Icon name="check-square" /> : <Icon name="square" />}
                    </div>
                )}

                {/* Hover Overlay with Actions */}
                {isSelectMode ? null : (
                    <div class="photo_overlay_actions">
                        <button 
                            type="button" 
                            class="action_btn download_btn" 
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(photo.photo_id, photo.photo_name);
                            }}
                            title={t({ zh: "下载原图", en: "Download Original" })}
                            aria-label={t({ zh: "下载原图", en: "Download Original" })}
                        >
                            <Icon name="arrow-down" />
                        </button>
                        <button
                            type="button"
                            class="action_btn compare_btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate("/compare_photo", {
                                    replace: false,
                                    state: {
                                        cameraId: selectedCameraId,
                                        photoId: photo.photo_id,
                                        photoTime: photo.photo_time,
                                        compareSide: "L",
                                    },
                                });
                            }}
                            title={t({ zh: "照片对比 (作为 L)", en: "Compare (as L)" })}
                            aria-label={t({ zh: "照片对比 (作为 L)", en: "Compare (as L)" })}
                        >
                            <Icon name="columns" />
                        </button>
                        <button 
                            type="button" 
                            class="action_btn info_btn" 
                            onClick={(e) => {
                                e.stopPropagation();
                                setViewingPhoto(photo);
                            }}
                            title={t({ zh: "查看详情", en: "View Details" })}
                            aria-label={t({ zh: "查看详情", en: "View Details" })}
                        >
                            <Icon name="info" />
                        </button>
                        {!isSubAccount && (
                            <button 
                                type="button" 
                                class="action_btn delete_btn" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(photo.photo_id);
                                }}
                                title={t({ zh: "删除照片", en: "Delete Photo" })}
                                aria-label={t({ zh: "删除照片", en: "Delete Photo" })}
                            >
                                <Icon name="trash-2" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }, [t, props.onPhotoSelected, selectedCameraId, selectedPhotos, isSubAccount, activePhotoId, expandedPhotoId, photoList, isLatest, requestScope]);

    const photoRenderList = useMemo(() => {
        // Sort photos by time DESC (Newest first)
        // This ensures the Leftmost photo is the Nearest/Latest time.
        const sortedList = [...photoList].sort((a, b) => b.photo_time - a.photo_time);
        return sortedList.map((photo, idx) => renderPhotoGridItem(photo, idx));
    }, [photoList, renderPhotoGridItem]);

    return (
        <div style="width: 100%; height: 100%; display: flex; flex-direction: column;">
            {(loadError || pageRefresh.error) && <p role="alert">{loadError || pageRefresh.error}</p>}
            {(pageRefresh.pendingRefresh || loadError || pageRefresh.error) && <div>
                {pageRefresh.pendingRefresh && <small>{t({ zh: "页面已恢复，可更新照片", en: "Page resumed; photos can be refreshed" })}</small>}
                <button class="outline" disabled={isBusy || pageRefresh.refreshing} onClick={pageRefresh.refreshNow}>
                    {t({ zh: "刷新到最新", en: "Refresh latest" })}
                </button>
            </div>}
            {/* 相机时间日期选择 */}
            <div class="farpov_photo_grid_header">
                {/* Left: Navigation & Filter */}
                <div class="header-left">
                    {(props.hideCameraSelector) ? null : (
                        <FarpovCameraSelect
                            disabled={isBusy}
                            selectCameraId={selectedCameraId}
                            setSelectCameraId={setSelectedCameraId}
                        />
                    )}
                    
                    <div role="group" class="date-input-group">
                        <input
                            type="datetime-local"
                            name="datetime-local"
                            step="1"
                            value={selectedTime.localTimeISOString}
                            onChange={selectedTime.datetimeLocalOnChange}
                        />
                        <button
                            type="button"
                            disabled={isBusy}
                            onClick={gotoAction}
                            /* Remove contrast class to fix dark color issue */
                        >
                            {t({ zh: "跳转", en: "Goto" })}
                        </button>
                    </div>
                </div>
                
                {/* Right: Tools & Actions */}
                {photoList.length > 0 && (
                    <div class="header-right" style={{ display: "flex", gap: "0.5rem" }}>
                        {/* Persistent Tools */}
                        <div role="group" class="tools-group" style={{ marginBottom: 0, display: "flex", width: "auto" }}>
                             <button 
                                type="button"
                                class="outline secondary"
                                onClick={() => {
                                    setRangeStart("1");
                                    setRangeEnd(totalPhotos.toString());
                                    setShowRangeModal(true);
                                }}
                                title={t({ zh: "按范围下载", en: "Range Download" })}
                            >
                                <Icon name="list" /> 
                            </button>
                            <button 
                                type="button"
                                class="outline secondary"
                                onClick={toggleSelectAll}
                                title={t({ zh: "全选本页", en: "Select All Page" })}
                                style={{ justifyContent: "center" }}
                            >
                                {isAllPageSelected ? <Icon name="check-square" /> : <Icon name="square" />} 
                                <span class="btn-text" style={{ marginLeft: "0.5rem" }}>{t({ zh: "全选", en: "All" })}</span>
                            </button>
                            {!isSubAccount && (
                                <button
                                    type="button"
                                    class="outline delete"
                                    style={{ borderColor: "var(--pico-del-color)", color: "var(--pico-del-color)", justifyContent: "center" }}
                                    onClick={handleDeleteAll}
                                    title={t({ zh: "清空所有照片", en: "Delete All Photos" })}
                                >
                                    <Icon name="trash" /> 
                                    <span class="btn-text" style={{ marginLeft: "0.5rem" }}>{t({ zh: "清空", en: "Clear All" })}</span>
                                </button>
                            )}
                        </div>

                        {/* Contextual Actions (Selected) */}
                        {selectedPhotos.size > 0 && (
                            <div role="group" class="actions-group" style={{ marginBottom: 0, display: "flex", width: "auto" }}>
                                <button
                                    type="button"
                                    class="outline"
                                    disabled={isBusy}
                                    onClick={handleBatchDownload}
                                    title={t({ zh: "下载选中", en: "Download Selected" })}
                                >
                                    <Icon name="download" /> {selectedPhotos.size}
                                </button>
                                {!isSubAccount && (
                                    <button
                                        type="button"
                                        class="outline delete"
                                        disabled={isBusy}
                                        onClick={handleBatchDelete}
                                        style={{ borderColor: "var(--pico-del-color)", color: "var(--pico-del-color)" }}
                                        title={t({ zh: "删除选中", en: "Delete Selected" })}
                                    >
                                        <Icon name="trash-2" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <hr />
            <div class="farpov_photo_grid_grid">
                {/* 照片列表 */}
                {photoRenderList}
            </div>
            {renderRangeModal()}
            {renderDeleteAllModal()}
            {renderDetailModal()}
            <form onSubmit={preventDefaultCallback} style="flex: 0 0 auto; margin-top: var(--pico-spacing);">
                {photoList.length > 0
                    ? (
                        <div class="grid">
                            <button
                                type="button"
                                disabled={isBusy}
                                onClick={previousPageAction}
                            >
                                {t({ zh: "上一页", en: "< Page" })}
                            </button>
                            <button
                                type="button"
                                disabled={isBusy}
                                onClick={nextPageAction}
                            >
                                {t({ zh: "下一页", en: "Page >" })}
                            </button>
                        </div>
                    )
                    : null}
            </form>
        </div>
    );
};

export default PhotoGrid;
